if (!customElements.get('uvp-ticker')) {
  customElements.define(
    'uvp-ticker',
    class UvpTicker extends HTMLElement {
      #resizeObserver = null;
      #mediaQuery = null;
      #viewport = null;
      #track = null;
      #primaryGroup = null;
      #control = null;
      #isPlaying = true;
      #userStartedPlayback = false;
      #pausedByHover = false;
      #boundUpdate = null;
      #boundTogglePlay = null;
      #boundMouseEnter = null;
      #boundMouseLeave = null;

      connectedCallback() {
        this.#viewport = this.querySelector('.uvp-ticker__viewport');
        this.#track = this.querySelector('.uvp-ticker__track');
        this.#primaryGroup = this.querySelector('.uvp-ticker__group:not([aria-hidden="true"])');
        this.#control = this.querySelector('.uvp-ticker__control');

        this.#isPlaying = this.dataset.autoplay === 'true';
        this.#boundUpdate = this.#updateAnimationState.bind(this);
        this.#boundTogglePlay = this.#togglePlay.bind(this);
        this.#boundMouseEnter = this.#onPointerEnter.bind(this);
        this.#boundMouseLeave = this.#onPointerLeave.bind(this);

        this.#mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

        if (this.#mediaQuery.matches) {
          this.#isPlaying = false;
        }

        this.#control?.addEventListener('click', this.#boundTogglePlay);
        this.#mediaQuery.addEventListener('change', this.#boundUpdate);

        if (this.dataset.pauseOnHover === 'true') {
          this.addEventListener('mouseenter', this.#boundMouseEnter);
          this.addEventListener('mouseleave', this.#boundMouseLeave);
          this.addEventListener('focusin', this.#boundMouseEnter);
          this.addEventListener('focusout', this.#boundMouseLeave);
        }

        this.#resizeObserver = new ResizeObserver(this.#boundUpdate);

        if (this.#viewport) {
          this.#resizeObserver.observe(this.#viewport);
        }

        if (this.#primaryGroup) {
          this.#resizeObserver.observe(this.#primaryGroup);
        }

        this.#updateAnimationState();
      }

      disconnectedCallback() {
        this.#resizeObserver?.disconnect();
        this.#mediaQuery?.removeEventListener('change', this.#boundUpdate);
        this.#control?.removeEventListener('click', this.#boundTogglePlay);
        this.removeEventListener('mouseenter', this.#boundMouseEnter);
        this.removeEventListener('mouseleave', this.#boundMouseLeave);
        this.removeEventListener('focusin', this.#boundMouseEnter);
        this.removeEventListener('focusout', this.#boundMouseLeave);
      }

      #getDisplayMode() {
        return this.dataset.displayMode || 'ticker';
      }

      #hasOverflow() {
        if (!this.#primaryGroup || !this.#viewport) {
          return false;
        }

        return this.#primaryGroup.scrollWidth > this.#viewport.clientWidth;
      }

      #canAnimate() {
        if (this.#getDisplayMode() === 'static') {
          return false;
        }

        if (!this.#hasOverflow()) {
          return false;
        }

        if (this.#mediaQuery?.matches && !this.#userStartedPlayback) {
          return false;
        }

        return this.#isPlaying;
      }

      #updateAnimationState() {
        const isAnimating = this.#canAnimate();
        const hasOverflow = this.#hasOverflow();
        const isStaticMode = this.#getDisplayMode() === 'static' || !hasOverflow;

        this.classList.toggle('uvp-ticker--animating', isAnimating);
        this.classList.toggle('uvp-ticker--static-layout', isStaticMode);

        if (this.#track) {
          this.#track.style.animationPlayState = isAnimating ? 'running' : 'paused';
        }

        this.#updateControl();
      }

      #updateControl() {
        if (!this.#control) {
          return;
        }

        const hasOverflow = this.#hasOverflow();
        const isTickerMode = this.#getDisplayMode() === 'ticker';
        const showControl = isTickerMode && hasOverflow;

        this.#control.hidden = !showControl;

        if (!showControl) {
          return;
        }

        const isAnimating = this.#canAnimate();
        this.#control.setAttribute('aria-pressed', isAnimating ? 'true' : 'false');
        this.#control.setAttribute(
          'aria-label',
          isAnimating ? this.#control.dataset.labelPause : this.#control.dataset.labelPlay
        );
      }

      #togglePlay() {
        if (!this.#hasOverflow() || this.#getDisplayMode() === 'static') {
          return;
        }

        this.#userStartedPlayback = true;
        this.#isPlaying = !this.#isPlaying;
        this.#pausedByHover = false;
        this.dataset.allowMotion = 'true';
        this.#updateAnimationState();
      }

      #onPointerEnter() {
        if (this.dataset.pauseOnHover !== 'true') {
          return;
        }

        if (this.#getDisplayMode() === 'static' || !this.#hasOverflow() || !this.#isPlaying) {
          return;
        }

        if (this.#mediaQuery?.matches && !this.#userStartedPlayback) {
          return;
        }

        this.#pausedByHover = true;
        this.classList.remove('uvp-ticker--animating');

        if (this.#track) {
          this.#track.style.animationPlayState = 'paused';
        }

        this.#updateControl();
      }

      #onPointerLeave(event) {
        if (!this.#pausedByHover) {
          return;
        }

        if (event.type === 'focusout' && this.contains(event.relatedTarget)) {
          return;
        }

        this.#pausedByHover = false;
        this.#updateAnimationState();
      }
    }
  );
}
